import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { ScrollableContainer } from '../../../components/ui/ScrollableContainer';
import { DataTable } from '../../../components/ui/DataTable';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiCheck, FiX, FiEye, FiEdit, FiRefreshCw } from 'react-icons/fi';
import {
  fetchPettyCashExpenses,
  checkPettyCashExpense,
  approvePettyCashExpense,
  rejectPettyCashExpense,
  updatePettyCashExpense,
  PettyCashExpense,
} from '../service/pettyCashService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${({ active }) =>
    active ? theme.colors.primary[500] : theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  background-color: ${({ active }) =>
    active ? theme.colors.primary[500] : theme.colors.white};
  color: ${({ active }) =>
    active ? theme.colors.white : theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active }) =>
    active ? theme.colors.primary[600] : theme.colors.light.background.secondary};
    border-color: ${({ active }) =>
    active ? theme.colors.primary[600] : theme.colors.light.border.medium};
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
    switch (status) {
      case 'approved':
        return `${theme.colors.accent.success}20`;
      case 'rejected':
        return `${theme.colors.accent.danger}20`;
      case 'checked':
        return `${theme.colors.accent.warning}20`;
      case 'pending':
        return `${theme.colors.primary[500]}20`;
      default:
        return `${theme.colors.light.border.medium}20`;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'approved':
        return theme.colors.accent.success;
      case 'rejected':
        return theme.colors.accent.danger;
      case 'checked':
        return theme.colors.accent.warning;
      case 'pending':
        return theme.colors.primary[600];
      default:
        return theme.colors.light.text.secondary;
    }
  }};
`;

const ActionButton = styled.button<{ variant?: 'success' | 'danger' | 'warning' | 'default' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${({ variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.accent.success;
      case 'danger':
        return theme.colors.accent.danger;
      case 'warning':
        return theme.colors.accent.warning;
      default:
        return theme.colors.light.text.secondary;
    }
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: ${theme.spacing[2]};

  &:hover {
    background-color: ${({ variant }) => {
    switch (variant) {
      case 'success':
        return `${theme.colors.accent.success}20`;
      case 'danger':
        return `${theme.colors.accent.danger}20`;
      case 'warning':
        return `${theme.colors.accent.warning}20`;
      default:
        return theme.colors.light.background.tertiary;
    }
  }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AmountCell = styled.div`
  text-align: right;
  color: ${theme.colors.accent.danger};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily.primary};
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.light.text.muted};
  }
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.primary[600]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[700]};
  }
`;

const SubmissionRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.primary[50]} !important;
  }
`;

interface SubmissionGroup {
  id: string;
  submittedAt: Date | any;
  submittedBy: string;
  submissionPeriod: 'daily' | 'weekly' | 'monthly';
  expenses: PettyCashExpense[];
  totalAmount: number;
  status: 'pending' | 'checked' | 'approved' | 'rejected';
}

const ExpenseManagementPage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const companyId = companyData?.id || user?.uid || '';

  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail ||
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for expense management
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditExpenseManagement = isCompanyOwner || (isStaffUser && staffData?.permissions?.expenseManagement === 'edit');

  const [expenses, setExpenses] = useState<PettyCashExpense[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'checked' | 'approved' | 'rejected' | 'all'>('pending');
  const [selectedExpense, setSelectedExpense] = useState<PettyCashExpense | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showSubmissionDetailModal, setShowSubmissionDetailModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionGroup | null>(null);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [editFormData, setEditFormData] = useState({
    date: '',
    amount: '',
    category: '',
    description: '',
    vendor: '',
    paymentMethod: 'cash',
    ref: '',
  });

  useEffect(() => {
    if (companyId) {
      loadExpenses();
    }
  }, [companyId, statusFilter]);

  const loadExpenses = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = statusFilter === 'all'
        ? await fetchPettyCashExpenses(companyId)
        : await fetchPettyCashExpenses(companyId, statusFilter);
      setExpenses(data);
    } catch (error: any) {
      // Log full error to console
      console.error('Error loading expenses:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      // Check if it's an index error
      if (error.code === 'failed-precondition' ||
        error.message?.includes('index') ||
        error.message?.includes('The query requires an index')) {
        console.error('⚠️ Firestore Index Error Detected!');
        console.error('This query requires a composite index. Please create the index at:');
        console.error('https://console.firebase.google.com/v1/r/project/guard-crm/firestore/indexes');
        toast.error('Database index required. Please check the console for details.');
      } else {
        toast.error(error.message || 'Failed to load expenses');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async (expenseId: string) => {
    setProcessing(true);
    try {
      await checkPettyCashExpense(companyId, expenseId);
      toast.success('Expense marked as checked');
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to check expense');
    } finally {
      setProcessing(false);
    }
  };

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [expenseToApprove, setExpenseToApprove] = useState<string | null>(null);

  const handleApprove = async (expenseId: string) => {
    setExpenseToApprove(expenseId);
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!expenseToApprove) return;

    setProcessing(true);
    try {
      await approvePettyCashExpense(companyId, expenseToApprove);
      toast.success('Expense approved and added to expenses');
      setShowApproveModal(false);
      setExpenseToApprove(null);
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve expense');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    // If we have selected expenses from modal, use bulk reject
    if (selectedExpenseIds.length > 0) {
      await handleBulkReject();
      return;
    }

    // Otherwise, handle single expense rejection
    if (!selectedExpense) {
      toast.error('No expense selected for rejection');
      return;
    }

    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await rejectPettyCashExpense(companyId, selectedExpense.id, rejectionReason.trim());
      toast.success('Expense rejected');
      setShowRejectModal(false);
      setSelectedExpense(null);
      setRejectionReason('');
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject expense');
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (expense: PettyCashExpense) => {
    setSelectedExpense(expense);
    let expenseDate: Date;
    if (expense.date instanceof Date) {
      expenseDate = expense.date;
    } else if (expense.date && typeof (expense.date as any).toDate === 'function') {
      expenseDate = (expense.date as any).toDate();
    } else if (expense.date && typeof expense.date === 'object' && (expense.date as any).seconds) {
      expenseDate = new Date((expense.date as any).seconds * 1000);
    } else {
      expenseDate = new Date(expense.date as any);
    }

    setEditFormData({
      date: expenseDate.toISOString().split('T')[0],
      amount: expense.amount.toString(),
      category: expense.category,
      description: expense.description,
      vendor: expense.vendor || '',
      paymentMethod: expense.paymentMethod || 'cash',
      ref: expense.ref || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateExpense = async () => {
    if (!selectedExpense) return;

    setProcessing(true);
    try {
      await updatePettyCashExpense(companyId, selectedExpense.id, {
        date: new Date(editFormData.date),
        amount: parseFloat(editFormData.amount),
        category: editFormData.category,
        description: editFormData.description,
        vendor: editFormData.vendor || undefined,
        paymentMethod: editFormData.paymentMethod || undefined,
        ref: editFormData.ref || undefined,
        submissionStatus: 'pending', // Reset to pending after edit
      });
      toast.success('Expense updated and resubmitted for review');
      setShowEditModal(false);
      setSelectedExpense(null);
      await loadExpenses();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update expense');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Group expenses by submission
  const groupExpensesBySubmission = (expenses: PettyCashExpense[]): SubmissionGroup[] => {
    const groups = new Map<string, PettyCashExpense[]>();

    expenses.forEach((expense) => {
      if (!expense.submittedAt) return;

      let submittedAt: Date;
      if (expense.submittedAt instanceof Date) {
        submittedAt = expense.submittedAt;
      } else if (expense.submittedAt && typeof (expense.submittedAt as any).toDate === 'function') {
        submittedAt = (expense.submittedAt as any).toDate();
      } else if (expense.submittedAt && typeof expense.submittedAt === 'object' && (expense.submittedAt as any).seconds) {
        submittedAt = new Date((expense.submittedAt as any).seconds * 1000);
      } else {
        submittedAt = new Date(expense.submittedAt as any);
      }

      const timestamp = Math.floor(submittedAt.getTime() / 1000) * 1000;
      const key = `${timestamp}_${expense.submittedBy || 'unknown'}_${expense.submissionPeriod || 'daily'}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(expense);
    });

    return Array.from(groups.entries()).map(([key, expenses]) => {
      const firstExpense = expenses[0];
      let submittedAt: Date;
      if (firstExpense.submittedAt instanceof Date) {
        submittedAt = firstExpense.submittedAt;
      } else if (firstExpense.submittedAt && typeof (firstExpense.submittedAt as any).toDate === 'function') {
        submittedAt = (firstExpense.submittedAt as any).toDate();
      } else if (firstExpense.submittedAt && typeof firstExpense.submittedAt === 'object' && (firstExpense.submittedAt as any).seconds) {
        submittedAt = new Date((firstExpense.submittedAt as any).seconds * 1000);
      } else {
        submittedAt = new Date(firstExpense.submittedAt as any);
      }

      const statuses = expenses.map(e => e.submissionStatus);
      const statusCounts = statuses.reduce((acc, status) => {
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      const mostCommonStatus = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'pending';

      return {
        id: key,
        submittedAt,
        submittedBy: firstExpense.submittedBy || 'unknown',
        submissionPeriod: firstExpense.submissionPeriod || 'daily',
        expenses: expenses.sort((a, b) => {
          let dateA: Date;
          if (a.date instanceof Date) {
            dateA = a.date;
          } else if (a.date && typeof (a.date as any).toDate === 'function') {
            dateA = (a.date as any).toDate();
          } else if (a.date && typeof a.date === 'object' && (a.date as any).seconds) {
            dateA = new Date((a.date as any).seconds * 1000);
          } else {
            dateA = new Date(a.date as any);
          }

          let dateB: Date;
          if (b.date instanceof Date) {
            dateB = b.date;
          } else if (b.date && typeof (b.date as any).toDate === 'function') {
            dateB = (b.date as any).toDate();
          } else if (b.date && typeof b.date === 'object' && (b.date as any).seconds) {
            dateB = new Date((b.date as any).seconds * 1000);
          } else {
            dateB = new Date(b.date as any);
          }

          return dateB.getTime() - dateA.getTime();
        }),
        totalAmount: expenses.reduce((sum, e) => sum + e.amount, 0),
        status: mostCommonStatus as 'pending' | 'checked' | 'approved' | 'rejected',
      };
    }).sort((a, b) => {
      const dateA = a.submittedAt instanceof Date ? a.submittedAt : new Date(a.submittedAt);
      const dateB = b.submittedAt instanceof Date ? b.submittedAt : new Date(b.submittedAt);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const submissionGroups = groupExpensesBySubmission(expenses);
  const totalPages = Math.ceil(submissionGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubmissions = submissionGroups.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewSubmission = (submission: SubmissionGroup) => {
    setSelectedSubmission(submission);
    setSelectedExpenseIds([]);
    setShowSubmissionDetailModal(true);
  };

  const handleToggleSelect = (expenseId: string) => {
    setSelectedExpenseIds((prev: string[]) =>
      prev.includes(expenseId)
        ? prev.filter((id: string) => id !== expenseId)
        : [...prev, expenseId]
    );
  };

  const handleSelectAll = () => {
    if (!selectedSubmission) return;
    if (selectedExpenseIds.length === selectedSubmission.expenses.length) {
      setSelectedExpenseIds([]);
    } else {
      setSelectedExpenseIds(selectedSubmission.expenses.map((e: PettyCashExpense) => e.id));
    }
  };

  const handleBulkCheck = async () => {
    if (selectedExpenseIds.length === 0) {
      toast.error('Please select at least one expense');
      return;
    }

    setProcessing(true);
    try {
      for (const expenseId of selectedExpenseIds) {
        await checkPettyCashExpense(companyId, expenseId);
      }
      toast.success(`Successfully checked ${selectedExpenseIds.length} expense(s)`);

      // Update local state instead of refetching everything inside the loop
      await loadExpenses();

      if (selectedSubmission) {
        // Fetch updated data for the specific submission
        const updated = await fetchPettyCashExpenses(companyId, statusFilter === 'all' ? undefined : statusFilter as any);
        const updatedSubmission = groupExpensesBySubmission(updated).find((s: SubmissionGroup) => s.id === selectedSubmission.id);

        if (updatedSubmission && updatedSubmission.expenses.length > 0) {
          setSelectedSubmission(updatedSubmission);
        } else {
          // No more expenses in this submission with current status
          setShowSubmissionDetailModal(false);
          setSelectedSubmission(null);
        }
      }
      setSelectedExpenseIds([]);
    } catch (error: any) {
      toast.error(error.message || 'Failed to check expenses');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedExpenseIds.length === 0) {
      toast.error('Please select at least one expense');
      return;
    }

    setProcessing(true);
    try {
      for (const expenseId of selectedExpenseIds) {
        await approvePettyCashExpense(companyId, expenseId);
      }
      toast.success(`Successfully approved ${selectedExpenseIds.length} expense(s)`);

      await loadExpenses();

      if (selectedSubmission) {
        const updated = await fetchPettyCashExpenses(companyId, statusFilter === 'all' ? undefined : statusFilter as any);
        const updatedSubmission = groupExpensesBySubmission(updated).find((s: SubmissionGroup) => s.id === selectedSubmission.id);

        if (updatedSubmission && updatedSubmission.expenses.length > 0) {
          setSelectedSubmission(updatedSubmission);
        } else {
          setShowSubmissionDetailModal(false);
          setSelectedSubmission(null);
        }
      }
      setSelectedExpenseIds([]);
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve expenses');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedExpenseIds.length === 0) {
      toast.error('Please select at least one expense');
      return;
    }

    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      for (const expenseId of selectedExpenseIds) {
        await rejectPettyCashExpense(companyId, expenseId, rejectionReason.trim());
      }
      toast.success(`Successfully rejected ${selectedExpenseIds.length} expense(s)`);

      setRejectionReason('');
      setShowRejectModal(false);

      await loadExpenses();

      if (selectedSubmission) {
        const updated = await fetchPettyCashExpenses(companyId, statusFilter === 'all' ? undefined : statusFilter as any);
        const updatedSubmission = groupExpensesBySubmission(updated).find((s: SubmissionGroup) => s.id === selectedSubmission.id);

        if (updatedSubmission && updatedSubmission.expenses.length > 0) {
          setSelectedSubmission(updatedSubmission);
        } else {
          setShowSubmissionDetailModal(false);
          setSelectedSubmission(null);
        }
      }
      setSelectedExpenseIds([]);
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject expenses');
    } finally {
      setProcessing(false);
    }
  };

  const expenseCategories = [
    'Office Supplies',
    'Transportation',
    'Meals & Entertainment',
    'Utilities',
    'Maintenance',
    'Communication',
    'Miscellaneous',
  ];

  return (
    <DashboardLayout>
      <ScrollableContainer>
        <PageHeader>
          <PageTitle>Expense Management</PageTitle>
          <Button onClick={loadExpenses} disabled={loading}>
            <FiRefreshCw size={18} style={{ marginRight: theme.spacing[2] }} />
            Refresh
          </Button>
        </PageHeader>

        <FilterContainer>
          <FilterButton
            active={statusFilter === 'pending'}
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </FilterButton>
          <FilterButton
            active={statusFilter === 'checked'}
            onClick={() => setStatusFilter('checked')}
          >
            Checked
          </FilterButton>
          <FilterButton
            active={statusFilter === 'approved'}
            onClick={() => setStatusFilter('approved')}
          >
            Approved
          </FilterButton>
          <FilterButton
            active={statusFilter === 'rejected'}
            onClick={() => setStatusFilter('rejected')}
          >
            Rejected
          </FilterButton>
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All
          </FilterButton>
        </FilterContainer>

        <Card>
          <CardBody>
            {loading ? (
              <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
                Loading expenses...
              </div>
            ) : submissionGroups.length > 0 ? (
              <DataTable
                header={
                  <tr>
                    <th>Submitted Date</th>
                    <th>Submission Period</th>
                    <th>Number of Expenses</th>
                    <th style={{ textAlign: 'right' }}>Total Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                }
                body={
                  <>
                    {paginatedSubmissions.map((submission) => (
                      <SubmissionRow
                        key={submission.id}
                        onClick={() => handleViewSubmission(submission)}
                      >
                        <td>{formatDate(submission.submittedAt)}</td>
                        <td>
                          {submission.submissionPeriod
                            ? submission.submissionPeriod.charAt(0).toUpperCase() + submission.submissionPeriod.slice(1)
                            : '—'}
                        </td>
                        <td>{submission.expenses.length}</td>
                        <td style={{ textAlign: 'right' }}>
                          <AmountCell>{formatCurrency(submission.totalAmount)}</AmountCell>
                        </td>
                        <td>
                          <StatusBadge status={submission.status}>
                            {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                          </StatusBadge>
                        </td>
                        <td>
                          <ViewButton
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              handleViewSubmission(submission);
                            }}
                            title="View Details"
                          >
                            <FiEye size={18} />
                          </ViewButton>
                        </td>
                      </SubmissionRow>
                    ))}
                  </>
                }
                pagination={{
                  currentPage,
                  totalPages,
                  totalItems: submissionGroups.length,
                  pageSize: itemsPerPage,
                  onPageChange: handlePageChange,
                  loading,
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
                <p>No expenses found for the selected status.</p>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Submission Detail Modal */}
        <Modal
          isOpen={showSubmissionDetailModal}
          onClose={() => {
            setShowSubmissionDetailModal(false);
            setSelectedSubmission(null);
            setSelectedExpenseIds([]);
          }}
          title="Submission Details"
          size="xl"
          footer={
            <>
              {canEditExpenseManagement && (
                <div style={{ display: 'flex', gap: theme.spacing[2], flex: 1 }}>
                  <Button
                    variant="info"
                    onClick={handleBulkCheck}
                    disabled={processing || selectedExpenseIds.length === 0}
                    loading={processing}
                  >
                    Check Selected ({selectedExpenseIds.length})
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleBulkApprove}
                    disabled={processing || selectedExpenseIds.length === 0}
                    loading={processing}
                  >
                    Approve Selected ({selectedExpenseIds.length})
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (selectedExpenseIds.length === 0) {
                        toast.error('Please select at least one expense');
                        return;
                      }
                      setRejectionReason('');
                      setShowRejectModal(true);
                    }}
                    disabled={processing || selectedExpenseIds.length === 0}
                  >
                    Reject Selected ({selectedExpenseIds.length})
                  </Button>
                </div>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  setShowSubmissionDetailModal(false);
                  setSelectedSubmission(null);
                  setSelectedExpenseIds([]);
                }}
              >
                Close
              </Button>
            </>
          }
        >
          {selectedSubmission && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: theme.spacing[4] }}>
                <div>
                  <Label>Submitted Date</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    {formatDate(selectedSubmission.submittedAt)}
                  </div>
                </div>
                <div>
                  <Label>Submission Period</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    {selectedSubmission.submissionPeriod
                      ? selectedSubmission.submissionPeriod.charAt(0).toUpperCase() + selectedSubmission.submissionPeriod.slice(1)
                      : '—'}
                  </div>
                </div>
                <div>
                  <Label>Number of Expenses</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    {selectedSubmission.expenses.length}
                  </div>
                </div>
                <div>
                  <Label>Total Amount</Label>
                  <div style={{ marginTop: theme.spacing[1], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.accent.danger }}>
                    {formatCurrency(selectedSubmission.totalAmount)}
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div style={{ marginTop: theme.spacing[1] }}>
                    <StatusBadge status={selectedSubmission.status}>
                      {selectedSubmission.status.charAt(0).toUpperCase() + selectedSubmission.status.slice(1)}
                    </StatusBadge>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing[2] }}>
                  <Label style={{ margin: 0 }}>
                    Expenses ({selectedSubmission.expenses.length})
                  </Label>
                  {canEditExpenseManagement && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {selectedExpenseIds.length === selectedSubmission.expenses.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>
                <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'visible', width: '100%' }}>
                  <div style={{ width: '100%', overflowX: 'visible' }}>
                    <Table>
                      <Table.Header>
                        <Table.Row>
                          {canEditExpenseManagement && (
                            <Table.HeaderCell style={{ width: '50px' }}>
                              <input
                                type="checkbox"
                                checked={selectedExpenseIds.length === selectedSubmission.expenses.length && selectedSubmission.expenses.length > 0}
                                onChange={handleSelectAll}
                              />
                            </Table.HeaderCell>
                          )}
                          <Table.HeaderCell style={{ minWidth: '100px' }}>Date</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '120px' }}>Category</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '200px' }}>Description</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '120px' }}>Vendor</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '130px' }}>Payment Method</Table.HeaderCell>
                          <Table.HeaderCell style={{ textAlign: 'right', minWidth: '100px' }}>Amount</Table.HeaderCell>
                          <Table.HeaderCell style={{ minWidth: '100px' }}>Status</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {selectedSubmission.expenses.map((expense: PettyCashExpense) => (
                          <Table.Row key={expense.id}>
                            {canEditExpenseManagement && (
                              <Table.Cell>
                                <input
                                  type="checkbox"
                                  checked={selectedExpenseIds.includes(expense.id)}
                                  onChange={() => handleToggleSelect(expense.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Table.Cell>
                            )}
                            <Table.Cell>{formatDate(expense.date)}</Table.Cell>
                            <Table.Cell>{expense.category || '—'}</Table.Cell>
                            <Table.Cell>{expense.description || '—'}</Table.Cell>
                            <Table.Cell>{expense.vendor || '—'}</Table.Cell>
                            <Table.Cell>{expense.paymentMethod || '—'}</Table.Cell>
                            <Table.Cell style={{ textAlign: 'right' }}>
                              <AmountCell>{formatCurrency(expense.amount)}</AmountCell>
                            </Table.Cell>
                            <Table.Cell>
                              <StatusBadge status={expense.submissionStatus}>
                                {expense.submissionStatus.charAt(0).toUpperCase() + expense.submissionStatus.slice(1)}
                              </StatusBadge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Reject Modal */}
        <Modal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedExpense(null);
            setRejectionReason('');
          }}
          title={selectedExpenseIds.length > 0 ? `Reject ${selectedExpenseIds.length} Expense(s)` : "Reject Expense"}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedExpense(null);
                  setRejectionReason('');
                }}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                loading={processing}
                disabled={processing || !rejectionReason.trim()}
              >
                {selectedExpenseIds.length > 0 ? `Reject ${selectedExpenseIds.length} Expense(s)` : 'Reject'}
              </Button>
            </>
          }
        >
          <FormGroup>
            <Label>Rejection Reason *</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection"
              required
              rows={4}
            />
          </FormGroup>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedExpense(null);
          }}
          title="Edit Expense"
          size="lg"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedExpense(null);
                }}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateExpense}
                loading={processing}
                disabled={processing}
              >
                Update & Resubmit
              </Button>
            </>
          }
        >
          <FormRow>
            <FormGroup>
              <Label>Date *</Label>
              <Input
                type="date"
                value={editFormData.date}
                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                required
                dark
              />
            </FormGroup>
            <FormGroup>
              <Label>Amount *</Label>
              <Input
                type="text"
                value={editFormData.amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setEditFormData({ ...editFormData, amount: value });
                  }
                }}
                placeholder="0.00"
                required
                dark
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Category *</Label>
              <Select
                value={editFormData.category}
                onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                required
              >
                <option value="">Select Category</option>
                {expenseCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Payment Method</Label>
              <Select
                value={editFormData.paymentMethod}
                onChange={(e) => setEditFormData({ ...editFormData, paymentMethod: e.target.value })}
              >
                <option value="cash">Cash</option>
                <option value="bank">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Description *</Label>
            <Input
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              placeholder="Enter expense description"
              required
              dark
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Vendor</Label>
              <Input
                value={editFormData.vendor}
                onChange={(e) => setEditFormData({ ...editFormData, vendor: e.target.value })}
                placeholder="Vendor name"
                dark
              />
            </FormGroup>
            <FormGroup>
              <Label>Reference</Label>
              <Input
                value={editFormData.ref}
                onChange={(e) => setEditFormData({ ...editFormData, ref: e.target.value })}
                placeholder="Reference number"
                dark
              />
            </FormGroup>
          </FormRow>
        </Modal>

        {/* Approve Confirmation Modal */}
        <Modal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setExpenseToApprove(null);
          }}
          title="Approve Expense"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowApproveModal(false);
                  setExpenseToApprove(null);
                }}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmApprove}
                loading={processing}
                disabled={processing}
              >
                Approve
              </Button>
            </>
          }
        >
          <p>Are you sure you want to approve this expense? It will be added to the expenses list and transactions.</p>
        </Modal>
      </ScrollableContainer>
    </DashboardLayout>
  );
};

export default ExpenseManagementPage;
